<script lang="ts" setup>
import { computed, PropType } from "vue";
import {
  WorkspaceDocumentFragment,
  ChildWorkspaceFragment,
  useReorderDocumentMutation,
} from "@/graphql/operations";
import { Draggable } from "@/components";
import DocListItem from "./DocListItem.vue";
import { useStore } from "vuex";
import WorkspaceOrderList from "@/components/workspace-ordering/WorkspaceOrderList.vue";
import type { WorkspaceMove } from "@/composables/useWorkspaceOrdering";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t } = useI18n();

const props = defineProps({
  subfolders: {
    type: Object as PropType<Array<ChildWorkspaceFragment>>,
    required: true,
  },
  documents: {
    type: Object as PropType<Array<WorkspaceDocumentFragment>>,
    required: true,
  },
  subfolderOrdering: {
    type: Boolean,
    default: false,
  },
  subfolderOrderingDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  edit: [id: string];
  share: [id: string];
  move: [id: string];
  moveSubfolder: [move: WorkspaceMove];
  subfolderOrderingGesture: [active: boolean];
}>();

const { executeMutation: reorderDocument } = useReorderDocumentMutation();
async function placeAbove(docId: string, aboveId: string) {
  await reorderDocument({
    documentId: docId,
    aboveId: aboveId,
  });
}

async function placeBelow(docId: string, belowId: string) {
  await reorderDocument({
    documentId: docId,
    belowId: belowId,
  });
}

const isAdmin = computed(
  () => store.getters.currentWorkspace?.root?.myPrivileges?.mayAdministrate,
);
</script>

<template>
  <div v-if="subfolders.length > 0" class="subfolder_list">
    <p v-if="subfolderOrdering" class="ordering_instructions">
      {{ $t("workspace.ordering.instructions") }}
    </p>
    <WorkspaceOrderList
      :items="subfolders"
      :editing="subfolderOrdering"
      :disabled="subfolderOrderingDisabled"
      :handle-label="t('workspace.ordering.moveHandle')"
      :instructions="t('workspace.ordering.instructions')"
      @move="emit('moveSubfolder', $event)"
      @gesture="emit('subfolderOrderingGesture', $event)"
    >
      <template #item="{ item: subfolder, canDrag, handleProps }">
        <div class="subfolder_row">
          <button
            v-if="subfolderOrdering && canDrag"
            type="button"
            class="subfolder_drag_handle"
            v-bind="handleProps"
          >
            <span class="material-symbols-outlined">drag_indicator</span>
          </button>
          <span
            v-else-if="subfolderOrdering"
            class="subfolder_drag_placeholder"
            aria-hidden="true"
          />
          <div class="subfolder_icon">
            <router-link
              :to="{ name: 'Workspace', params: { workspaceId: subfolder.id } }"
            >
              <div class="icon">
                <span class="material-symbols-outlined filled">folder</span>
              </div>
            </router-link>
          </div>
          <div class="subfolder_details">
            <router-link
              :to="{ name: 'Workspace', params: { workspaceId: subfolder.id } }"
            >
              <div>
                <div class="details">{{ subfolder.name }}</div>
                <div class="subinfo">
                  {{ $t("workspace.subfolder.label") }}
                  &middot;
                  {{
                    $t("workspace.subfolder.numItems", {
                      count:
                        subfolder.documents.totalCount +
                        subfolder.childWorkspaces.totalCount,
                    })
                  }}
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </template>
    </WorkspaceOrderList>
  </div>
  <div>
    <Draggable
      @placeBelow="placeBelow"
      @placeAbove="placeAbove"
      :numItems="documents.length"
      :disabled="!isAdmin"
    >
      <DocListItem
        v-for="(doc, idx) in documents"
        :key="doc!.id"
        :doc="doc"
        @edit="emit('edit', doc!.id)"
        @share="emit('share', doc.id)"
        @move="emit('move', doc.id)"
        :data-id="doc.id"
        :data-idx="idx"
        :data-index="idx"
      />
    </Draggable>
  </div>
</template>

<style lang="scss" scoped>
.subfolder_list {
  margin-bottom: 0.7rem;
}

.ordering_instructions {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.8rem;
  margin: 0 0 0.45rem;
}

.subfolder_row {
  align-items: center;
  background: var(--classic-surface, #fff);
  border-bottom: 1px solid var(--classic-border, #dbdbdb);
  display: flex;
  min-height: 62px;
  padding: 0 0.55rem;
}

.subfolder_row:hover {
  background: var(--classic-surface-hover, #f7f7f7);
}

.subfolder_drag_handle,
.subfolder_drag_placeholder {
  flex: 0 0 34px;
  width: 34px;
}

.subfolder_drag_handle {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--classic-text-secondary, #777);
  cursor: grab;
  display: inline-flex;
  justify-content: center;
  padding: 0.35rem;
}

.subfolder_drag_handle:active {
  cursor: grabbing;
}

@media (max-width: 767px), (any-pointer: coarse) {
  .subfolder_drag_handle,
  .subfolder_drag_placeholder {
    flex-basis: 44px;
    min-height: 44px;
    width: 44px;
  }
}

.subfolder_icon {
  flex: 0 0 48px;
}

.subfolder_details {
  flex: 1;
  line-height: 1.2;
  min-width: 0;
}

td.menu {
  width: 30px;
}

.icon {
  width: 40px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

a {
  color: var(--classic-text, #363636);
  text-decoration: none;
}

.icon .material-symbols-outlined {
  font-size: 3rem;
  color: var(--classic-folder, #a1c9f7);
}

.details {
  font-size: 0.9rem;
  margin-top: 0.4em;
  margin-left: 0.2em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subinfo {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.8em;
  margin-top: 0.2em;
  margin-left: 0.2em;
}

.thumbnail_container {
  height: 60px;
  width: 60px;
  padding: 0.5em 0;
}

.thumbnail_container a {
  height: 100%;
  width: 100%;
  background-color: var(--classic-media-surface, #fafafa);
  display: inline-block;
}

img.thumbnail_image {
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
  height: 100%;
  width: 100%;
}

.info {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.9em;
  margin-top: 0.5em;
}

.menu .material-symbols-outlined {
  font-size: 1.2em;
  padding: 0.58em;
  line-height: 1;
}

.doc_title .material-symbols-outlined {
  font-size: 1.2em;
}

.tags {
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 0.9em;
  margin-top: 0.6em;
  margin-bottom: 0.8em;
}

.tag {
  background-color: var(--classic-tag-surface, #eaeaea);
  padding: 0.5em 0.8em;
  border-radius: 2em;
  margin-right: 0.3em;
  display: inline-block;
  font-size: 0.9em;
}
</style>
